import React, { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Plus, Trash2, Columns3, Rows3, HelpCircle, CircleCheck, ArrowRight } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { BlockLine } from './BlockLine';
import { ScaleToFit } from '../ScaleToFit';
import { EditableLabel } from './EditableLabel';
import { useMathEducation, type ManipulativeState } from '../MathEducationProvider';
import type { BlockLine as BlockLineType } from '../../types/blocks';
import { generateBlockColor } from '../../types/blocks';
import { getAchievementMetadata } from '../achievements/achievementMetadata';
import { getAchievementHint, findLineByLabel } from '../hints';
import {
  TUTORIAL_CHAPTERS,
  TUTORIAL_RESCUE_DELAY_MS,
  advanceTutorialStage,
  getCurrentChapter,
  getTutorialPulse,
  getTutorialStage,
  isChapterComplete,
  isTutorialComplete,
  type BlockEdgePulse,
  type LabelPulse,
} from '../tutorial/tutorialStages';
import { WorkspaceHeader } from './WorkspaceHeader';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Panel } from '../ui/panel';

/** How long the stage-cleared checkmark stays up after a tutorial stage clears. */
const TUTORIAL_STAGE_FLASH_MS = 900;

/**
 * Motion props for a round workspace button — the pivot button and the multiplier button, which
 * the tutorial highlights the way `Block` highlights an edge.
 *
 * When pulsing, this replaces the button's resting entrance animation wholesale, so `opacity: 1`
 * has to be restated: these buttons animate in from `opacity: 0`, and keyframes that omitted it
 * would leave a pulsing invisible button. The 1s cadence matches the block-edge pulse, so the two
 * highlights read as one language.
 */
const controlButtonMotion = (pulsing: boolean, restTransition: { duration: number; delay?: number }) =>
  pulsing
    ? {
        animate: {
          opacity: 1,
          scale: [1, 1.12, 1],
          boxShadow: [
            '0 1px 2px rgba(0, 0, 0, 0.08), 0 0 0 0 rgba(34, 197, 94, 0)',
            '0 1px 2px rgba(0, 0, 0, 0.08), 0 0 0 10px rgba(34, 197, 94, 0.35)',
            '0 1px 2px rgba(0, 0, 0, 0.08), 0 0 0 0 rgba(34, 197, 94, 0)',
          ],
        },
        transition: { duration: 1, repeat: Infinity, ease: 'easeInOut' as const },
      }
    : { animate: { opacity: 1, scale: 1 }, transition: restTransition };

// Separate component for ratio instance to properly use hooks
interface RatioInstanceProps {
  lines: BlockLineType[];
  arrangement: 'parallel' | 'sequence';
  showDelete: boolean;
  showLabels: boolean;
  cloneMultiplier: number;
  totalValuesCreated: number;
  onAddBlock: (lineId: string) => void;
  onRemoveBlock: (lineId: string) => void;
  onAddLine: () => void;
  onRemoveLine: (lineId: string) => void;
  onDecrementMultiplier: () => void;
  onLabelChange: (lineId: string, label: string) => void;
  onToggleArrangement: () => void;
  readOnly?: boolean;
  /** Tutorial highlight for one line's add/remove edge, or null when nothing is highlighted. */
  pulseTarget?: BlockEdgePulse | null;
  /** Tutorial highlight for the pivot button, which sits outside the block rows. */
  pulseArrangement?: boolean;
  /** Tutorial highlight for one line's name, in the labels column beside the rows. */
  pulseLabel?: LabelPulse | null;
}

function RatioInstance({
  lines,
  arrangement,
  showDelete,
  showLabels,
  cloneMultiplier,
  totalValuesCreated,
  onAddBlock,
  onRemoveBlock,
  onAddLine,
  onRemoveLine,
  onDecrementMultiplier,
  onLabelChange,
  onToggleArrangement,
  readOnly = false,
  pulseTarget = null,
  pulseArrangement = false,
  pulseLabel = null,
}: RatioInstanceProps) {
  const [isHovered, setIsHovered] = useState(false);

  // Calculate total blocks across all values
  const totalBlocks = lines.reduce((sum, line) => sum + line.blocks * cloneMultiplier, 0);

  // Determine if remove button should be hidden: hide if only 1 block in only 1 line
  const shouldHideRemove = lines.length === 1 && lines[0].blocks === 1;

  if (arrangement === 'parallel') {
    // Parallel layout: labels to the left of rows
    return (
      <div
        className="tw-relative tw-group tw-flex tw-gap-4"
        onMouseEnter={() => showDelete && setIsHovered(true)}
        onMouseLeave={() => showDelete && !readOnly && setIsHovered(false)}
      >
        {/* Invisible hover zone - extends the hoverable area to where the delete button appears */}
        {showDelete && !readOnly && (
          <div
            className="tw-absolute"
            style={{
              top: '-50px',
              right: '-50px',
              width: '80px',
              height: '80px',
              zIndex: 15,
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          />
        )}

        {/* Total indicator - only show on first instance in parallel */}
        {showLabels && (
          <div
            className="tw-absolute tw-flex tw-items-center tw-whitespace-nowrap"
            style={{
              top: '-48px',
              left: '0',
              gap: 'var(--spacing-2, 8px)',
            }}
          >
            <span
              style={{
                color: 'var(--muted-foreground)',
                fontSize: '14px',
              }}
            >
              Total:
            </span>
            <span
              style={{
                color: 'var(--foreground)',
                fontSize: '16px',
              }}
            >
              {totalBlocks}
            </span>
          </div>
        )}

        {/* Delete button - appears on hover, absolutely positioned */}
        <AnimatePresence>
          {showDelete && !readOnly && isHovered && (
            <Tooltip>
              <TooltipTrigger asChild>
                <motion.button
                  onClick={onDecrementMultiplier}
                  onMouseEnter={() => setIsHovered(true)}
                  className="tw-absolute tw-bg-white tw-text-neutral-500 hover:tw-text-red-600 tw-transition-colors tw-flex tw-items-center tw-justify-center"
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    border: '1px solid var(--border)',
                    boxShadow: 'var(--elevation-md)',
                    top: '-32px',
                    right: '-32px',
                    zIndex: 20,
                    pointerEvents: 'auto',
                  }}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0 }}
                  transition={{ duration: 0.2 }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Trash2 className="tw-w-5 tw-h-5" />
                </motion.button>
              </TooltipTrigger>
              <TooltipContent>
                <span className="tw-text-white">Remove Multiple</span>
              </TooltipContent>
            </Tooltip>
          )}
        </AnimatePresence>

        {/* Labels column */}
        {showLabels && (
          <div className="tw-flex tw-flex-col tw-gap-4" style={{ minWidth: '120px' }}>
            {lines.map((line, index) => {
              const total = line.blocks * cloneMultiplier;
              return (
                <div key={line.id} className="tw-flex tw-items-center" style={{ height: '48px' }}>
                  {readOnly ? (
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <div
                        className="tw-w-3 tw-h-3 tw-rounded-sm"
                        style={{
                          backgroundColor: line.color,
                        }}
                      />
                      <span className="tw-text-sm tw-text-neutral-700">
                        {(line.label || 'Value').trim()} ({total})
                      </span>
                    </div>
                  ) : (
                    <EditableLabel
                      value={line.label || ''}
                      color={line.color}
                      total={total}
                      onChange={newLabel => onLabelChange(line.id, newLabel)}
                      onDelete={() => onRemoveLine(line.id)}
                      canDelete={lines.length > 1}
                      pulse={pulseLabel?.lineIndex === index}
                    />
                  )}
                </div>
              );
            })}
            {!readOnly && <div style={{ height: '48px' }} />}
          </div>
        )}

        {/* Block content */}
        <div className="tw-flex tw-flex-col tw-gap-4 tw-relative">
          {/* Pivot button - positioned above blocks */}
          {showLabels && !readOnly && (
            <Tooltip>
              <TooltipTrigger asChild>
                <motion.button
                  onClick={onToggleArrangement}
                  className="tw-absolute tw-z-10 tw-bg-white tw-text-neutral-500 hover:tw-text-neutral-950 tw-transition-colors tw-flex tw-items-center tw-justify-center tw-cursor-pointer"
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    border: '1px solid var(--border)',
                    boxShadow: 'var(--elevation-sm)',
                    top: '-56px',
                    left: '4px',
                  }}
                  initial={{ opacity: 0, scale: 0 }}
                  {...controlButtonMotion(pulseArrangement, { duration: 0.3 })}
                  whileHover={{ scale: 1.05, rotate: -90 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Rows3 className="tw-w-4 tw-h-4" />
                </motion.button>
              </TooltipTrigger>
              <TooltipContent>
                <span className="tw-text-white">Switch to Sequence</span>
              </TooltipContent>
            </Tooltip>
          )}
          <AnimatePresence mode="popLayout">
            {lines.map((line, index) => (
              <motion.div
                key={line.id}
                className="tw-flex tw-items-center tw-justify-start"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <BlockLine
                  blocks={line.blocks}
                  color={line.color}
                  label={line.label}
                  index={index}
                  onAdd={() => onAddBlock(line.id)}
                  onRemove={() => onRemoveBlock(line.id)}
                  hideRemove={shouldHideRemove}
                  interactive={!readOnly}
                  pulse={pulseTarget?.lineIndex === index ? pulseTarget.side : undefined}
                />
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Add Value button */}
          {!readOnly && (
            <motion.button
              onClick={onAddLine}
              className="tw-bg-neutral-100 tw-text-neutral-500 hover:tw-text-neutral-950 hover:tw-bg-neutral-200 tw-transition-colors tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-cursor-pointer tw-w-fit tw-whitespace-nowrap"
              style={{
                borderRadius: 'var(--radius-button)',
                border: '1px solid var(--border)',
              }}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0 }}
              transition={{ duration: 0.3 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div
                className="tw-w-3 tw-h-3 tw-flex-shrink-0"
                style={{
                  backgroundColor: generateBlockColor(totalValuesCreated),
                  borderRadius: 'var(--radius-sm)',
                }}
              />
              <span className="tw-text-sm tw-font-atkinson">Add value</span>
              <Plus className="tw-w-4 tw-h-4 tw-flex-shrink-0" />
            </motion.button>
          )}
        </div>
      </div>
    );
  } else {
    // Sequenced layout: labels above columns
    return (
      <div
        className="tw-relative tw-group"
        onMouseEnter={() => showDelete && !readOnly && setIsHovered(true)}
        onMouseLeave={() => showDelete && !readOnly && setIsHovered(false)}
      >
        {/* Invisible hover zone - extends the hoverable area to where the delete button appears */}
        {showDelete && !readOnly && (
          <div
            className="tw-absolute"
            style={{
              top: '0',
              left: '-100px',
              width: '100px',
              height: '100%',
              zIndex: 15,
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          />
        )}

        {/* Total indicator - only show on first instance in sequenced */}
        {showLabels && !readOnly && (
          <div
            className="tw-absolute tw-flex tw-flex-col tw-items-center tw-justify-center tw-whitespace-nowrap"
            style={{
              left: '-80px',
              top: '50%',
              transform: 'translateY(-50%)',
              gap: 'var(--spacing-1, 4px)',
            }}
          >
            <span
              style={{
                color: 'var(--muted-foreground)',
                fontSize: '14px',
              }}
            >
              Total
            </span>
            <span
              style={{
                color: 'var(--foreground)',
                fontSize: '20px',
              }}
            >
              {totalBlocks}
            </span>
          </div>
        )}

        {/* Pivot button - only show on first instance */}
        {showLabels && (
          <Tooltip>
            <TooltipTrigger asChild>
              <motion.button
                onClick={onToggleArrangement}
                className="tw-absolute tw-z-10 tw-bg-white tw-text-neutral-500 hover:tw-text-neutral-950 tw-transition-colors tw-flex tw-items-center tw-justify-center tw-cursor-pointer"
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  border: '1px solid var(--border)',
                  boxShadow: 'var(--elevation-sm)',
                  top: '-56px',
                  left: '-16px',
                }}
                initial={{ opacity: 0, scale: 0 }}
                {...controlButtonMotion(pulseArrangement, { duration: 0.3 })}
                whileHover={{ scale: 1.05, rotate: 90 }}
                whileTap={{ scale: 0.95 }}
              >
                <Columns3 className="tw-w-4 tw-h-4" />
              </motion.button>
            </TooltipTrigger>
            <TooltipContent>
              <span className="tw-text-white">Switch to Parallel</span>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Delete button - appears on hover, absolutely positioned */}
        <AnimatePresence>
          {showDelete && !readOnly && isHovered && (
            <Tooltip>
              <TooltipTrigger asChild>
                <motion.button
                  onClick={onDecrementMultiplier}
                  onMouseEnter={() => setIsHovered(true)}
                  className="tw-absolute tw-bg-white tw-text-neutral-500 hover:tw-text-red-600 tw-transition-colors tw-flex tw-items-center tw-justify-center"
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    border: '1px solid var(--border)',
                    boxShadow: 'var(--elevation-md)',
                    left: '-56px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    zIndex: 20,
                    pointerEvents: 'auto',
                  }}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0 }}
                  transition={{ duration: 0.2 }}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Trash2 className="tw-w-5 tw-h-5" />
                </motion.button>
              </TooltipTrigger>
              <TooltipContent>
                <span className="tw-text-white">Remove Multiple</span>
              </TooltipContent>
            </Tooltip>
          )}
        </AnimatePresence>

        {/* Labels row above blocks */}
        {showLabels && (
          <div className="tw-flex tw-items-center tw-justify-start tw-gap-6 tw-mb-4">
            {lines.map((line, index) => {
              const total = line.blocks * cloneMultiplier;
              return (
                <div key={line.id} className="tw-flex tw-items-center tw-justify-center">
                  {readOnly ? (
                    <span className="tw-text-sm tw-text-neutral-700">
                      {(line.label || 'Value').trim()} ({total})
                    </span>
                  ) : (
                    <EditableLabel
                      value={line.label || ''}
                      color={line.color}
                      total={total}
                      onChange={newLabel => onLabelChange(line.id, newLabel)}
                      onDelete={() => onRemoveLine(line.id)}
                      canDelete={lines.length > 1}
                      pulse={pulseLabel?.lineIndex === index}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Block content */}
        <div className="tw-flex tw-items-center tw-justify-start tw-gap-6">
          <AnimatePresence mode="popLayout">
            {lines.map((line, index) => (
              <motion.div
                key={line.id}
                className="tw-flex tw-items-center tw-justify-start"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <BlockLine
                  blocks={line.blocks}
                  color={line.color}
                  label={line.label}
                  index={index}
                  onAdd={() => onAddBlock(line.id)}
                  onRemove={() => onRemoveBlock(line.id)}
                  hideRemove={shouldHideRemove}
                  interactive={!readOnly}
                  pulse={pulseTarget?.lineIndex === index ? pulseTarget.side : undefined}
                />
              </motion.div>
            ))}
          </AnimatePresence>

          {/* Add Value button */}
          {!readOnly && (
            <motion.button
              onClick={onAddLine}
              className="tw-bg-neutral-100 tw-text-neutral-500 hover:tw-text-neutral-950 hover:tw-bg-neutral-200 tw-transition-colors tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-cursor-pointer tw-w-fit tw-whitespace-nowrap"
              style={{
                borderRadius: 'var(--radius-button)',
                border: '1px solid var(--border)',
              }}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0 }}
              transition={{ duration: 0.3 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div
                className="tw-w-3 tw-h-3 tw-flex-shrink-0"
                style={{
                  backgroundColor: generateBlockColor(totalValuesCreated),
                  borderRadius: 'var(--radius-sm)',
                }}
              />
              <span className="tw-text-sm tw-font-atkinson">Add value</span>
              <Plus className="tw-w-4 tw-h-4 tw-flex-shrink-0" />
            </motion.button>
          )}
        </div>
      </div>
    );
  }
}

interface BlockBuilderProps {
  achievementId: string | null;
  currentLines: BlockLineType[];
  currentArrangement: 'parallel' | 'sequence';
  currentMultiplier: number;
  displayOnly?: boolean;
  displayLines?: BlockLineType[];
  displayArrangement?: 'parallel' | 'sequence';
  displayMultiplier?: number;
  hideLabels?: boolean;
  /** Scale based on width and derive height from content instead of filling parent. */
  autoHeight?: boolean;
  /** Max height (px) when autoHeight is true. */
  maxHeight?: number;
}

export function BlockBuilder({
  achievementId,
  currentLines,
  currentArrangement,
  currentMultiplier,
  displayOnly = false,
  displayLines,
  displayArrangement,
  displayMultiplier,
  hideLabels = false,
  autoHeight = false,
  maxHeight,
}: BlockBuilderProps) {
  const {
    incrementAttempts,
    unlockAchievement,
    updateManipulativeState,
    progress,
    isTutorialRunning,
    completeTutorial,
  } = useMathEducation();
  const [lines, setLines] = useState<BlockLineType[]>([
    { id: '1', blocks: 3, color: generateBlockColor(0), label: 'Value A' },
    { id: '2', blocks: 2, color: generateBlockColor(1), label: 'Value B' },
  ]);
  const [arrangement, setArrangement] = useState<'parallel' | 'sequence'>('parallel');
  const [cloneMultiplier, setCloneMultiplier] = useState<number>(1);
  const [totalValuesCreated, setTotalValuesCreated] = useState<number>(2); // Track total values ever created
  const [showHintModal, setShowHintModal] = useState(false);
  const [showAlreadySolvedModal, setShowAlreadySolvedModal] = useState(false);

  // The Explore tutorial runs in place of the goal header until it has been finished once.
  // `displayOnly` excludes the read-only copy of this component that `ConnectSlides` renders.
  // The stage index is deliberately not persisted mid-run: the tutorial is four clicks long, so
  // restarting it after a reload costs less than the persistence dance a stage counter needs.
  const showTutorial = isTutorialRunning && !displayOnly;
  const [tutorialStage, setTutorialStage] = useState(0);
  const [showStageCleared, setShowStageCleared] = useState(false);
  // Whether the student has gone quiet long enough to be offered a hint on an unaided stage.
  const [isTutorialIdle, setIsTutorialIdle] = useState(false);
  const renderedLines = displayOnly && displayLines ? displayLines : lines;
  const renderedArrangement = displayOnly && displayArrangement ? displayArrangement : arrangement;
  const renderedMultiplier = displayOnly && typeof displayMultiplier === 'number' ? displayMultiplier : cloneMultiplier;

  // Get achievement metadata and hint
  const metadata = achievementId ? getAchievementMetadata(achievementId) : null;
  const hint = metadata
    ? getAchievementHint(achievementId!, currentLines, currentArrangement, currentMultiplier)
    : null;

  // The one snapshot every tutorial predicate reads. Hoisted out of the sync effect so the
  // advance effect depends on the same identity: a stage that turns on the arrangement or the
  // copy count cannot then fail to advance because an effect only watched `lines`.
  const workspaceState = React.useMemo<ManipulativeState>(
    () => ({ lines, arrangement, cloneMultiplier }),
    [lines, arrangement, cloneMultiplier],
  );

  const currentTutorialStage = getTutorialStage(tutorialStage);
  const tutorialPulse = showTutorial ? getTutorialPulse(tutorialStage, workspaceState, isTutorialIdle) : null;
  // Block edges and labels are reached through `RatioInstance`, down two different branches of
  // it; the pivot and multiplier buttons are its siblings, so they are highlighted here instead.
  const blockEdgePulse = tutorialPulse?.kind === 'block-edge' ? tutorialPulse : null;
  const labelPulse = tutorialPulse?.kind === 'label' ? tutorialPulse : null;
  const controlPulse = tutorialPulse?.kind === 'control' ? tutorialPulse.control : null;

  // Check if the current manipulative state satisfies the achievement goal
  const checkIfAchievementMet = useCallback(
    (id: string | null): boolean => {
      if (!id) return false;

      // Check for "Double" achievement - ratio of 1:2
      if (id === 'double' && lines.length === 2) {
        const [line1, line2] = lines;
        return line1.blocks === 1 && line2.blocks === 2;
      }

      // Check for "3 to 8" achievement - ratio of 3:8
      if (id === '3-to-8' && lines.length === 2) {
        const [line1, line2] = lines;
        return line1.blocks === 3 && line2.blocks === 8;
      }

      // Check for "8 to 3" achievement - ratio of 8:3
      if (id === '8-to-3' && lines.length === 2) {
        const [line1, line2] = lines;
        return line1.blocks === 8 && line2.blocks === 3;
      }

      // Check for "Sequenced" achievement - 1:2:3 ratio in sequence mode
      if (id === 'sequenced' && arrangement === 'sequence' && lines.length === 3) {
        const [line1, line2, line3] = lines;
        return line1.blocks === 1 && line2.blocks === 2 && line3.blocks === 3;
      }

      // Check for "Equivalent Ratios" achievement - 5:9 ratio with at least 5 multiples
      if (id === 'equivalent-ratios' && lines.length === 2) {
        const [line1, line2] = lines;
        return line1.blocks === 5 && line2.blocks === 9 && cloneMultiplier >= 5;
      }

      // Check for "Bikes" achievement - bikes and tires with 1:2 ratio (accepts singular or plural)
      if (id === 'bikes') {
        const bikesLine = findLineByLabel(lines, 'bike', 'bikes');
        const tiresLine = findLineByLabel(lines, 'tire', 'tires');
        return !!(bikesLine && tiresLine && bikesLine.blocks === 1 && tiresLine.blocks === 2);
      }

      // Check for "Multi-bike" achievement - bikes and tires with 1:2 ratio and multiple instances
      if (id === 'multi-bike') {
        const bikesLine = findLineByLabel(lines, 'bike', 'bikes');
        const tiresLine = findLineByLabel(lines, 'tire', 'tires');
        return !!(bikesLine && tiresLine && bikesLine.blocks === 1 && tiresLine.blocks === 2 && cloneMultiplier >= 2);
      }

      // Check for "Spiders" achievement - spiders:legs ratio (1:8) (accepts singular or plural)
      if (id === 'spiders') {
        const spidersLine = findLineByLabel(lines, 'spider', 'spiders');
        const legsLine = findLineByLabel(lines, 'leg', 'legs');
        return !!(spidersLine && legsLine && spidersLine.blocks === 1 && legsLine.blocks === 8);
      }

      // Check for "Part-to-Whole" achievement - red:white stripes (7:6)
      if (id === 'part-to-whole') {
        const redLine = lines.find(l => l.label?.toLowerCase().includes('red'));
        const whiteLine = lines.find(l => l.label?.toLowerCase().includes('white'));
        return !!(redLine && whiteLine && redLine.blocks === 7 && whiteLine.blocks === 6);
      }

      return false;
    },
    [lines, arrangement, cloneMultiplier],
  );

  // Check if the current manipulative state satisfies the selected achievement
  const isCurrentlyMet = checkIfAchievementMet(achievementId);

  // Advance the tutorial the moment a stage's target is reached. `advanceTutorialStage` returns
  // the index unchanged when the target is not met, so this settles rather than looping.
  React.useEffect(() => {
    if (!showTutorial) {
      return;
    }
    setTutorialStage(stage => advanceTutorialStage(stage, workspaceState));
  }, [showTutorial, workspaceState]);

  // Offer a hint once the student has stopped making progress. Any change to the workspace — or
  // reaching a new stage — restarts the clock, so the hint only ever appears to someone who has
  // actually stalled.
  React.useEffect(() => {
    if (!showTutorial) {
      return;
    }
    setIsTutorialIdle(false);
    const timer = window.setTimeout(() => setIsTutorialIdle(true), TUTORIAL_RESCUE_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [showTutorial, workspaceState, tutorialStage]);

  // Flash a confirmation each time a stage clears, so progress registers without having to read.
  React.useEffect(() => {
    if (tutorialStage === 0) {
      return;
    }
    setShowStageCleared(true);
    const timer = window.setTimeout(() => setShowStageCleared(false), TUTORIAL_STAGE_FLASH_MS);
    return () => window.clearTimeout(timer);
  }, [tutorialStage]);

  // Sync manipulative state to context whenever it changes
  React.useEffect(() => {
    if (displayOnly) {
      return;
    }
    updateManipulativeState(workspaceState);
  }, [displayOnly, workspaceState, updateManipulativeState]);

  const handleAddBlock = useCallback(
    (lineId: string) => {
      setLines(prev => prev.map(line => (line.id === lineId ? { ...line, blocks: line.blocks + 1 } : line)));
      incrementAttempts('block-builder');
    },
    [incrementAttempts],
  );

  const handleRemoveBlock = useCallback(
    (lineId: string) => {
      setLines(prev => {
        const line = prev.find(l => l.id === lineId);
        if (!line) return prev;

        // If only 1 block, remove the entire line (if more than 1 line exists)
        if (line.blocks === 1 && prev.length > 1) {
          return prev.filter(l => l.id !== lineId);
        }

        // Otherwise, just decrement the block count
        return prev.map(l => (l.id === lineId ? { ...l, blocks: Math.max(1, l.blocks - 1) } : l));
      });
      incrementAttempts('block-builder');
    },
    [incrementAttempts],
  );

  const handleToggleArrangement = useCallback(() => {
    setArrangement(prev => (prev === 'parallel' ? 'sequence' : 'parallel'));
    incrementAttempts('block-builder');
  }, [incrementAttempts]);

  const handleLabelChange = useCallback(
    (lineId: string, label: string) => {
      setLines(prev =>
        prev.map(line => {
          if (line.id === lineId) {
            return { ...line, label };
          }
          return line;
        }),
      );
    },
    [unlockAchievement],
  );

  const handleAddLine = useCallback(() => {
    const nextIndex = totalValuesCreated; // Use totalValuesCreated instead of lines.length
    const nextId = String(Math.max(...lines.map(l => parseInt(l.id, 10))) + 1);
    const nextLabel = `Value ${String.fromCharCode(65 + lines.length)}`; // Label based on current position

    setLines(prev => [
      ...prev,
      {
        id: nextId,
        blocks: 1,
        color: generateBlockColor(nextIndex),
        label: nextLabel,
      },
    ]);
    setTotalValuesCreated(prev => prev + 1); // Increment the counter
    incrementAttempts('block-builder');
  }, [lines, totalValuesCreated, incrementAttempts]);

  const handleRemoveLine = useCallback(
    (lineId: string) => {
      if (lines.length > 1) {
        setLines(prev => prev.filter(line => line.id !== lineId));
        incrementAttempts('block-builder');
      }
    },
    [lines.length, incrementAttempts],
  );

  const handleIncrementMultiplier = useCallback(() => {
    if (cloneMultiplier < 100) {
      setCloneMultiplier(prev => prev + 1);
      incrementAttempts('block-builder');
    }
  }, [cloneMultiplier, incrementAttempts]);

  const handleDecrementMultiplier = useCallback(() => {
    if (cloneMultiplier > 1) {
      setCloneMultiplier(prev => prev - 1);
      incrementAttempts('block-builder');
    }
  }, [cloneMultiplier, incrementAttempts]);

  const Wrapper = displayOnly ? 'div' : Panel;

  return (
    <>
      {/* Main workspace card */}
      <motion.div
        style={{
          height: autoHeight ? undefined : '100%',
          width: '100%',
          minWidth: 0,
          flexShrink: 1,
        }}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
      >
        <Wrapper
          className={displayOnly ? 'tw-relative tw-flex tw-flex-col' : undefined}
          style={{ height: autoHeight ? undefined : '100%', width: '100%', minWidth: 0, flexShrink: 1 }}
        >
          {/* Tutorial header - replaces the goal display until the tutorial has been finished */}
          {showTutorial && (
            <WorkspaceHeader
              icon="🧱"
              title="Getting Started"
              announcePromptChanges
              prompt={currentTutorialStage ? currentTutorialStage.prompt : "You've got it."}
            >
              {/* One dot per chapter rather than per stage. Four dots read as a short errand;
                  seven read as a chore, and the per-click confirmation is already the checkmark
                  that flashes over the blocks. */}
              <div className="tw-flex tw-items-center" style={{ gap: 'var(--spacing-2)' }} aria-hidden>
                {TUTORIAL_CHAPTERS.map(chapter => {
                  const cleared = isChapterComplete(chapter, tutorialStage);
                  const current = getCurrentChapter(tutorialStage) === chapter;
                  return (
                    <span
                      key={chapter}
                      style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        backgroundColor: cleared ? '#22c55e' : 'transparent',
                        border: `2px solid ${cleared || current ? '#22c55e' : 'var(--border)'}`,
                        transition: 'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out',
                      }}
                    />
                  );
                })}
              </div>

              {/* Nothing is being checked here — the student has finished the walkthrough and is
                  being handed the goal tree, so the button says that rather than "Check answer". */}
              {isTutorialComplete(tutorialStage) && (
                <Button
                  onClick={completeTutorial}
                  style={{
                    gap: 'var(--spacing-2)',
                    backgroundColor: '#22c55e',
                    borderColor: '#22c55e',
                    color: 'white',
                    cursor: 'pointer',
                  }}
                >
                  Start exploring
                  <ArrowRight style={{ width: '18px', height: '18px' }} />
                </Button>
              )}
            </WorkspaceHeader>
          )}

          {/* Goal Display Header - same sizing as the tutorial, so the step up in text size
              carries across the whole Explore section rather than just the walkthrough. */}
          {metadata && !displayOnly && !showTutorial && (
            <WorkspaceHeader icon={metadata.icon} title={metadata.title} prompt={metadata.shortDescription}>
              {isCurrentlyMet ? (
                <Button
                  onClick={() => {
                    const currentLevel = achievementId ? progress.achievements[achievementId] : undefined;
                    if (!currentLevel || currentLevel === 'empty') {
                      unlockAchievement(achievementId!);
                    } else {
                      setShowAlreadySolvedModal(true);
                    }
                  }}
                  style={{
                    gap: 'var(--spacing-2)',
                    backgroundColor: '#22c55e',
                    borderColor: '#22c55e',
                    color: 'white',
                    cursor: 'pointer',
                  }}
                >
                  <CircleCheck style={{ width: '18px', height: '18px' }} />
                  Check answer
                </Button>
              ) : (
                <Button variant="outline" onClick={() => setShowHintModal(true)} style={{ gap: 'var(--spacing-2)' }}>
                  <HelpCircle style={{ width: '18px', height: '18px' }} />
                  Get a hint
                </Button>
              )}
            </WorkspaceHeader>
          )}

          {/* Manipulative Workspace - grows to fill remaining space, centers content */}
          <div
            className={
              autoHeight
                ? 'tw-relative tw-flex tw-items-center tw-justify-center'
                : 'tw-relative tw-flex-1 tw-flex tw-items-center tw-justify-center'
            }
            style={autoHeight ? undefined : { minHeight: 0, overflow: 'hidden' }}
          >
            {/* Stage-cleared confirmation, over the blocks the student is already looking at
                rather than back up in the header. */}
            <AnimatePresence>
              {showStageCleared && (
                <motion.div
                  className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-pointer-events-none"
                  initial={{ opacity: 0, scale: 0.3 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.5 }}
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                  style={{ zIndex: 30 }}
                >
                  <CircleCheck
                    strokeWidth={2.5}
                    style={{
                      width: '160px',
                      height: '160px',
                      color: '#22c55e',
                      filter: 'drop-shadow(0 0 14px rgba(255, 255, 255, 0.95))',
                    }}
                  />
                </motion.div>
              )}
            </AnimatePresence>

            <ScaleToFit
              className={autoHeight ? 'tw-w-full' : 'tw-w-full tw-h-full'}
              padding={16}
              autoHeight={autoHeight}
              maxHeight={maxHeight}
            >
              <div
                className={
                  renderedArrangement === 'parallel'
                    ? 'tw-flex tw-gap-6 tw-items-center'
                    : 'tw-flex tw-flex-col tw-gap-6 tw-items-center'
                }
                style={{
                  // Add padding to account for absolutely positioned buttons
                  // Pivot button: left: -56px, top: -16px (parallel) or top: -56px, left: -16px (sequence)
                  // Delete buttons also positioned outside flow
                  paddingLeft: displayOnly ? '20px' : '60px',
                  paddingRight: displayOnly ? '20px' : '40px',
                  paddingTop: displayOnly ? '20px' : renderedArrangement === 'sequence' ? '60px' : '20px',
                  paddingBottom: '20px',
                }}
              >
                {Array.from({ length: renderedMultiplier }, (_, i) => (
                  <RatioInstance
                    key={i}
                    lines={renderedLines}
                    arrangement={renderedArrangement}
                    showDelete={i > 0}
                    showLabels={i === 0 && !hideLabels}
                    cloneMultiplier={renderedMultiplier}
                    totalValuesCreated={totalValuesCreated}
                    onAddBlock={handleAddBlock}
                    onRemoveBlock={handleRemoveBlock}
                    onAddLine={handleAddLine}
                    onRemoveLine={handleRemoveLine}
                    onDecrementMultiplier={handleDecrementMultiplier}
                    onLabelChange={handleLabelChange}
                    onToggleArrangement={handleToggleArrangement}
                    readOnly={displayOnly}
                    pulseTarget={i === 0 ? blockEdgePulse : null}
                    pulseArrangement={i === 0 && controlPulse === 'arrangement'}
                    pulseLabel={i === 0 ? labelPulse : null}
                  />
                ))}

                {/* Increment multiplier button */}
                {!displayOnly && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <motion.button
                        onClick={handleIncrementMultiplier}
                        className="tw-bg-white tw-text-neutral-500 hover:tw-text-neutral-950 tw-transition-colors tw-flex tw-items-center tw-justify-center tw-cursor-pointer"
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          border: '1px solid var(--border)',
                          boxShadow: 'var(--elevation-sm)',
                          fontFamily: 'var(--font-atkinson)',
                          fontSize: '16px',
                          fontWeight: 'var(--font-weight-semibold)',
                        }}
                        initial={{ opacity: 0, scale: 0 }}
                        {...controlButtonMotion(controlPulse === 'multiplier', { duration: 0.3, delay: 0.1 })}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {renderedArrangement === 'sequence' ? '↓' : '→'} {renderedMultiplier + 1}×
                      </motion.button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <span className="tw-text-white">Add Multiple</span>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            </ScaleToFit>
          </div>
        </Wrapper>
      </motion.div>

      {/* Hint Modal */}
      {metadata && hint && !displayOnly && (
        <Dialog open={showHintModal} onOpenChange={setShowHintModal}>
          <DialogContent
            style={{
              borderRadius: 'var(--radius-modal)',
              padding: 'var(--spacing-6)',
              gap: 'var(--spacing-4)',
            }}
          >
            <DialogHeader style={{ gap: 'var(--spacing-1)' }}>
              <div className="tw-flex tw-items-center" style={{ gap: 'var(--spacing-2)' }}>
                <span style={{ fontSize: '24px' }}>{metadata.icon}</span>
                <DialogTitle style={{ margin: 0 }}>{metadata.title}</DialogTitle>
              </div>

              {/* Problem prompt - de-emphasized and smaller */}
              <p
                style={{
                  color: 'var(--muted-foreground)',
                  margin: 0,
                  fontSize: '0.75rem',
                }}
              >
                {metadata.shortDescription}
              </p>
            </DialogHeader>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--spacing-4)',
                marginTop: 'var(--spacing-5)',
              }}
            >
              {hint.encouragement && (
                <div
                  style={{
                    padding: 'var(--spacing-3)',
                    backgroundColor: 'var(--success-bg)',
                    borderLeft: '3px solid var(--success)',
                    borderRadius: 'var(--radius)',
                  }}
                >
                  <p
                    style={{
                      margin: 0,
                      color: 'var(--success-foreground)',
                    }}
                  >
                    ✓ {hint.encouragement}
                  </p>
                </div>
              )}

              <DialogDescription style={{ margin: 0 }}>{hint.message}</DialogDescription>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Already Solved Modal */}
      {metadata && !displayOnly && (
        <Dialog open={showAlreadySolvedModal} onOpenChange={setShowAlreadySolvedModal}>
          <DialogContent
            style={{
              borderRadius: 'var(--radius-modal)',
              padding: 'var(--spacing-6)',
              gap: 'var(--spacing-4)',
            }}
          >
            <DialogHeader style={{ gap: 'var(--spacing-1)' }}>
              <div className="tw-flex tw-items-center" style={{ gap: 'var(--spacing-2)' }}>
                <span style={{ fontSize: '24px' }}>{metadata.icon}</span>
                <DialogTitle style={{ margin: 0 }}>{metadata.title}</DialogTitle>
              </div>
            </DialogHeader>

            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--spacing-4)',
                marginTop: 'var(--spacing-5)',
              }}
            >
              <div
                style={{
                  padding: 'var(--spacing-4)',
                  backgroundColor: '#d4edda',
                  borderLeft: '3px solid #22c55e',
                  borderRadius: 'var(--radius)',
                }}
              >
                <p style={{ margin: 0, color: '#155724' }}>Good job! You&apos;ve solved that problem before.</p>
              </div>

              <DialogDescription style={{ margin: 0 }}>
                Ready for a new challenge? Click on a different goal in the Problem Tree to continue exploring!
              </DialogDescription>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
